import './applyConfirmVehiclePage.scss';

import { ThemeProvider } from '@mui/material/styles';
import { ApplicationType } from '@thesavingsgroup/enums/ApplicationType';
import { isNonEmptyString } from '@thesavingsgroup/utils/isNonEmptyString';
import { toCurrencyString } from '@thesavingsgroup/utils/toCurrencyString';
import { toNumberString } from '@thesavingsgroup/utils/toNumberString';
import { toNumericString } from '@thesavingsgroup/utils/toNumericString';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import VehicleImagePlaceholder from '../../../assets/vehicle-placeholder.png';
import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import { Select } from '../../../components/Select/Select';
import { resetStore as defaultResetStore } from '../../../store/_core/resetStore';
import useApiStore from '../../../store/apiStore/useApiStore';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useDashboardStore from '../../../store/dashboardStore/useDashboardStore';
import { useNavigateWithCallback } from '../../../store/routeStore/hooks/useNavigateWithCallback';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { ApplyConfirmVehiclePageProps } from './types/ApplyConfirmVehiclePageProps';

export function Component({
  resetStore = defaultResetStore,
}: ApplyConfirmVehiclePageProps): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();
  const navigateWithCallback = useNavigateWithCallback();

  const { isLoading } = useApiStore();

  const {
    applicationType,
    computed: { yearMakeModel },
    fetchVehicleImageByTrim,
    fetchVehicleImageByTrimFailed,
    fetchVehicleTrims,
    fetchVehicleTrimsFailedMessage,
    isDuplicateVehicleError,
    mileage,
    payoffAmount,
    purchasePrice,
    resetSubmitValues,
    setMileage,
    setPayoffAmount,
    setPurchasePrice,
    setTrimByValue,
    submit,
    submitCompleted,
    submitFailedMessage,
    trim,
    trims,
    vehicleImage,
  } = useApplyStore();

  const { t } = useTranslation();
  const { previous } = useWorkflowStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      mileageValue: toNumberString(mileage),
      payoffAmountValue: toCurrencyString(payoffAmount),
      purchasePriceValue: toCurrencyString(purchasePrice),
      trimValue: get(trim, 'value', '') as string,
    },
    mode: 'onTouched',
    criteriaMode: 'firstError',
    delayError: 200,
  });

  const trimValidation = {
    required: t('validation.trim.required'),
  };

  const mileageValidation = {
    setValueAs: (value: string): string => toNumericString(value),
    required: t('validation.mileage.required'),
  };

  const payoffAmountValidation = {
    setValueAs: (value: string): string => toNumericString(value),
    required: t('validation.payoffAmount.required'),
    min: {
      value: 1,
      message: t('validation.payoffAmount.min', { min: '$0' }),
    },
    max: {
      value: 999999,
      message: t('validation.payoffAmount.max', { max: '$1,000,000' }),
    },
  };

  const purchasePriceValidation = {
    setValueAs: (value: string): string => toNumericString(value),
    required: t('validation.purchasePrice.required'),
    min: {
      value: 1,
      message: t('validation.purchasePrice.min', { min: '$0' }),
    },
    max: {
      value: 999999,
      message: t('validation.purchasePrice.max', { max: '$1,000,000' }),
    },
  };

  const [imageLoading, setImageLoading] = useState(false);

  const displayVehicleImage = useMemo((): boolean => {
    return vehicleImage && !imageLoading;
  }, [vehicleImage, imageLoading]);

  const displayPlaceholderImage = useMemo((): boolean => {
    return isEmpty(vehicleImage) || imageLoading;
  }, [vehicleImage, imageLoading]);

  const displayPayoffAmount = useMemo((): boolean => {
    return applicationType === ApplicationType.REFINANCE;
  }, [applicationType]);

  const displayPurchasePrice = useMemo((): boolean => {
    return (
      applicationType === ApplicationType.PURCHASE ||
      applicationType === ApplicationType.LEASE_BUYOUT
    );
  }, [applicationType]);

  const nextStep = (): void => {
    const {
      computed: { vehicleId },
    } = useDashboardStore.getState();

    // Reset applicant and coapplicant stores to ensure a clean state for the applicant workflow.
    resetStore(useApplicantStore);
    resetStore(useCoapplicantStore);

    navigateWithCallback(`/applicant/${vehicleId}`).then((): void => {
      // Reset the apply store to ensure a clean state for the next application.
      resetStore(useApplyStore);
    });
  };

  const previousStep = (): void => {
    navigate(previous());
  };

  // Register options hand the change event straight through, not the value.
  const reloadVehicleImage = (event: { target: { value: string } }): void => {
    const selectedTrim = trims.find(
      (trimObj): boolean => trimObj.value === event.target.value,
    );

    if (selectedTrim) {
      resetSubmitValues();

      setImageLoading(true);
      fetchVehicleImageByTrim(selectedTrim);
    }
  };

  const formSubmit = (values: FieldValues): Promise<void> => {
    const { trimValue, mileageValue, payoffAmountValue, purchasePriceValue } =
      values;

    if (
      !trimValue ||
      !mileageValue ||
      (displayPayoffAmount && !payoffAmountValue) ||
      (displayPurchasePrice && !purchasePriceValue)
    ) {
      return;
    }

    setTrimByValue(trimValue);
    setMileage(mileageValue);

    if (displayPayoffAmount) {
      setPayoffAmount(payoffAmountValue);
    }

    if (displayPurchasePrice) {
      setPurchasePrice(purchasePriceValue);
    }

    submit();
  };

  useEffect((): void => {
    resetSubmitValues();

    fetchVehicleTrims();

    if (!trim) {
      // when trim is not available, reset the vehicle image
      useApplyStore.setState({ vehicleImage: null });

      return;
    }

    setImageLoading(true);

    fetchVehicleImageByTrim(trim);
  }, []);

  useEffect((): void => {
    // image loading is tracked by the onLoad event of the image tag.
    // This is done to prevent the loading animation from ending upon the completion of the request alone,
    // whereas the animation should end when the image is fully loaded.

    // if the request for the image url failed, end the loading animation
    if (fetchVehicleImageByTrimFailed) {
      setImageLoading(false);
    }
  }, [fetchVehicleImageByTrimFailed]);

  useEffect((): void => {
    if (submitCompleted && !submitFailedMessage) {
      nextStep();
    }
  }, [submitCompleted, submitFailedMessage]);

  return (
    <ThemeProvider theme={theme}>
      <div className="page apply-confirm-vehicle-page">
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          splitLayout
          onBackClick={previousStep}
        />

        <main data-testid={locator.pages.applyConfirmVehicle.main.root}>
          <figure>
            <div>
              <img
                alt={t('imageAlts.selectedVehicle')}
                className={displayVehicleImage ? 'show' : 'hide'}
                data-testid={
                  locator.pages.applyConfirmVehicle.main.selectedVehicleImage
                }
                src={vehicleImage || undefined}
                onLoad={(): void => {
                  setImageLoading(false);
                }}
              />

              <img
                alt={t('imageAlts.selectedVehicle')}
                className={displayPlaceholderImage ? 'show' : 'hide'}
                data-testid={
                  locator.pages.applyConfirmVehicle.main.placeholderVehicleImage
                }
                src={VehicleImagePlaceholder}
              />

              {imageLoading && <span className="skeleton-loading-sweep" />}
            </div>
          </figure>
          <form
            data-testid={locator.pages.applyConfirmVehicle.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applyConfirmVehicle.form.h1}>
                {yearMakeModel}
              </h1>
              <h2 data-testid={locator.pages.applyConfirmVehicle.form.h2}>
                {t('ApplyConfirmVehiclePage.subtitle')}
              </h2>

              {!isEmpty(fetchVehicleTrimsFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applyConfirmVehicle.alert.vehicleTrimsError
                    }
                    severity="error"
                  >
                    {fetchVehicleTrimsFailedMessage}
                  </Alert>
                </div>
              )}

              {isNonEmptyString(submitFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applyConfirmVehicle.alert.submitFailedError
                    }
                    severity="error"
                  >
                    <Trans
                      components={{
                        dashboard: <Link to="/dashboard/garage" />,
                      }}
                    >
                      {submitFailedMessage}
                    </Trans>
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <Select
                    {...register('trimValue', {
                      ...trimValidation,
                      onChange: reloadVehicleImage,
                    })}
                    error={errors.trimValue}
                    errorTestId={
                      locator.pages.applyConfirmVehicle.form.trimSelectError
                    }
                    inputTestId={
                      locator.pages.applyConfirmVehicle.form.trimSelect
                    }
                    label={t('ApplyConfirmVehiclePage.trim')}
                    options={trims.map((trimObj) => ({
                      name: trimObj.label,
                      value: trimObj.value,
                    }))}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <NumberInput
                    {...register('mileageValue', mileageValidation)}
                    data-testid={
                      locator.pages.applyConfirmVehicle.form.mileageInput
                    }
                    error={errors.mileageValue}
                    errorTestId={
                      locator.pages.applyConfirmVehicle.form.mileageInputError
                    }
                    label={t('ApplyConfirmVehiclePage.mileage')}
                    min={0}
                    number
                  />
                </div>

                {displayPayoffAmount && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <NumberInput
                      {...register('payoffAmountValue', payoffAmountValidation)}
                      data-testid={
                        locator.pages.applyConfirmVehicle.form.payoffAmountInput
                      }
                      error={errors.payoffAmountValue}
                      errorTestId={
                        locator.pages.applyConfirmVehicle.form
                          .payoffAmountInputError
                      }
                      label={t('ApplyConfirmVehiclePage.payoffAmount')}
                      currency
                    />
                  </div>
                )}

                {displayPurchasePrice && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <NumberInput
                      {...register(
                        'purchasePriceValue',
                        purchasePriceValidation,
                      )}
                      data-testid={
                        locator.pages.applyConfirmVehicle.form
                          .purchasePriceInput
                      }
                      error={errors.purchasePriceValue}
                      errorTestId={
                        locator.pages.applyConfirmVehicle.form
                          .purchasePriceInputError
                      }
                      label={t('ApplyConfirmVehiclePage.purchasePrice')}
                      currency
                    />
                  </div>
                )}
              </div>

              <Button
                data-testid={
                  locator.pages.applyConfirmVehicle.form.submitButton
                }
                disabled={
                  (isLoading && !imageLoading) ||
                  isDuplicateVehicleError ||
                  !isValid
                }
                loader={isLoading && !imageLoading}
                loaderPosition="start"
                fullWidth
                submit
              >
                {t('ApplyConfirmVehiclePage.continue')}
              </Button>
            </div>
            <footer data-testid={locator.pages.applyConfirmVehicle.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplyConfirmVehiclePage';
